/**
 * Authoring-time warning for nodes that will not fit on the big screen without scrolling.
 *
 * The projector (`Screen.tsx`, `.screen-root` in `src/client/styles/pages.css`) is fixed to the
 * viewport and gives its main column a designed scroll region with a fade and a "more below"
 * chevron (see docs/design-system.md), so an over-long node is no longer a layout defect — but it
 * is still a facilitator-experience defect: a room reading off a projector cannot scroll it, so a
 * node that needs scrolling to be read in full should be trimmed or split, and an author should
 * find that out while writing, not during a live run. This module estimates whether a node's
 * rendered height at a reference projector would exceed one screen, and reports the ones that
 * would as `warning`-level `ValidationIssue`s — never `error`, because a long node still works, it
 * just scrolls.
 *
 * The estimate only covers `narrative`, `reveal` and `decision` nodes: the ones whose length is
 * the author's choice (free-form body text, an artefact, a list of lessons, a set of options).
 * `call` transcripts reveal progressively and are judged by lines-on-screen-at-once, which is
 * runtime state, not something a static node can predict; `poll`, `branch` and `debrief` either
 * cap their content already (poll options) or render inside a fundamentally different layout (the
 * debrief's radar chart), so they are out of scope here.
 *
 * Calibration: every constant below was measured, not guessed, against `dependency-worm.ts` in a
 * real Chromium at the reference size (1280×900, dark, `.screen-mode`) — see the comment on each
 * constant for which node produced it. Two independent nodes (the "intro" narrative-with-artifact
 * and the "reveal" node with a seven-item lesson list) reproduced their measured total height to
 * within one line once run through the formulas here, and the "decision-1" node's measured 5-line
 * prompt wrap confirms `TITLE_CHARS_PER_LINE`. Re-measure and update these constants if the
 * projector's type scale or spacing tokens change.
 */
import type { Scenario, ScenarioNode } from "./schema";
import type { ValidationIssue } from "./validate";

/** The viewport this estimate targets: the smallest width the audit treats as a projector. */
const REFERENCE_WIDTH = 1280;
const REFERENCE_HEIGHT = 900;

/**
 * `.screen-root`'s own padding is `3vh` top and bottom (see pages.css), and now that it is fixed
 * to the viewport (`height: 100dvh; overflow: hidden`) that padding is exactly what the main
 * column's scroll-free budget has to fit inside.
 */
const BUDGET_PX = REFERENCE_HEIGHT - 2 * 0.03 * REFERENCE_HEIGHT; // 846

/**
 * `.screen-body`'s font-size is `clamp(20px, 3.4vh, 48px)`, which evaluates to 30.6px at 900px
 * tall; its line-height is 1.45×. This is the estimate's unit: `lines` and `limit` are both counts
 * of this line height, so a node's whole estimated height is expressed in the same terms as the
 * text an author is actually looking at.
 */
const BODY_LINE_PX = 30.6 * 1.45; // 44.37
/**
 * Average characters that fit one wrapped `.screen-body` line at its `34ch` measure (674px wide
 * at 1280×900). Measured against the "intro" node's 663-character, two-paragraph body: it renders
 * as exactly 16 lines (9 + a blank line between paragraphs + 6) at chars-per-line 45.
 */
const BODY_CHARS_PER_LINE = 45;

/** `.eyebrow`'s font-size is fixed (`--text-xs`), not `vh`-scaled, so this measured height holds
 * at any projector width — confirmed identical (19.5px) on all three calibration nodes. */
const EYEBROW_PX = 19.5;
/** The `stack-lg` gap between blocks (`--s-8`), a fixed token, not `vh`-scaled. */
const GAP_PX = 32;

/**
 * The artefact card's header and body both render at `--text-sm` (15px, 22.5px line height) —
 * fixed tokens, not `vh`-scaled, so the card is the same size at any projector width.
 * `ARTIFACT_CHARS_PER_LINE` and `ARTIFACT_CHROME_PX` (its padding and borders) are both measured
 * against the "intro" node's email: a 205-character paragraph plus three short lines wraps to 9
 * lines in an 818px-wide card whose total height is 62px more than those 9 lines plus its 4
 * header rows (kind, from, to, subject) account for.
 */
const ARTIFACT_LINE_PX = 22.5;
const ARTIFACT_CHARS_PER_LINE = 110;
const ARTIFACT_CHROME_PX = 62;

/**
 * `.screen-title`'s font-size is `clamp(28px, 6vh, 84px)`, 54px at 900px tall, line-height 1.05,
 * plus its own `0.2em` margin top and bottom. `TITLE_CHARS_PER_LINE` is measured against the
 * "decision-1" node's 111-character prompt, in the ~827px-wide two-column layout at 1280px: it
 * wraps to exactly 5 lines.
 */
const TITLE_LINE_PX = 54 * 1.05; // 56.7
const TITLE_MARGIN_PX = 0.2 * 54; // 10.8
const TITLE_CHARS_PER_LINE = 25;

/**
 * `.screen-list`'s font-size is `clamp(18px, 3vh, 40px)`, 27px at 900px tall, line-height 1.4, plus
 * `--s-2` between items. Measured against the "reveal" node's seven lessons (88–172 characters
 * each, in the 827px-wide column): all seven reproduce their real 2-or-3-line wrap at
 * chars-per-line 65.
 */
const LIST_LINE_PX = 27 * 1.4; // 37.8
const LIST_CHARS_PER_LINE = 65;
const LIST_ITEM_GAP_PX = 8; // --s-2

/**
 * `.screen-option`'s row height for a one-line label, measured against "decision-1": three of its
 * four options render at exactly this height regardless of label length once the label wraps past
 * one line, the row simply grows — but per the design decision, the fit estimate counts every
 * option as one line, the same simplification the layout already makes forgivable (an
 * over-long option label is the author's problem to shorten, not the projector's to scroll for).
 */
const OPTION_ROW_PX = 121;
const OPTION_GAP_PX = 14.4; // `.screen-options`'s `1.6vh` gap at 900px tall

/** Lines a block of text wraps to at `charsPerLine`, one line per `\n`-separated segment (a blank
 * segment, i.e. a blank line between paragraphs, still takes one line — `white-space: pre-line`
 * renders it as an empty line box, not nothing). */
function wrappedLines(text: string, charsPerLine: number): number {
  return text
    .split("\n")
    .reduce((sum, segment) => sum + Math.max(1, Math.ceil(segment.length / charsPerLine)), 0);
}

/**
 * Estimated rendered height of `node` on the projector at 1280×900, and how much fits without the
 * main column's scroll region ever engaging — both expressed in `.screen-body` line heights. A
 * node the caller should warn about is one where `lines > limit`.
 */
export function estimateScreenFit(node: ScenarioNode): { lines: number; limit: number } {
  const limit = BUDGET_PX / BODY_LINE_PX;
  let px: number;
  switch (node.type) {
    case "narrative": {
      px = EYEBROW_PX + GAP_PX + wrappedLines(node.body, BODY_CHARS_PER_LINE) * BODY_LINE_PX;
      if (node.artifact) {
        const headerRows =
          1 + (node.artifact.from ? 1 : 0) + (node.artifact.to ? 1 : 0) + (node.artifact.subject ? 1 : 0);
        const bodyLines = wrappedLines(node.artifact.body, ARTIFACT_CHARS_PER_LINE);
        px += GAP_PX + (headerRows + bodyLines) * ARTIFACT_LINE_PX + ARTIFACT_CHROME_PX;
      }
      break;
    }
    case "reveal": {
      px =
        EYEBROW_PX +
        TITLE_MARGIN_PX * 2 +
        TITLE_LINE_PX +
        GAP_PX +
        wrappedLines(node.body, BODY_CHARS_PER_LINE) * BODY_LINE_PX;
      if (node.lessons.length) {
        const itemLines = node.lessons.reduce((sum, l) => sum + wrappedLines(l, LIST_CHARS_PER_LINE), 0);
        px += GAP_PX + EYEBROW_PX + itemLines * LIST_LINE_PX + (node.lessons.length - 1) * LIST_ITEM_GAP_PX;
      }
      break;
    }
    case "decision": {
      // The context paragraph and the vote timer are deliberately not modelled: see the design
      // decision in docs/scenario-authoring.md — options and the prompt are what an author
      // controls the length of, and are what this warning is for.
      const promptLines = wrappedLines(node.prompt, TITLE_CHARS_PER_LINE);
      px =
        EYEBROW_PX +
        TITLE_MARGIN_PX * 2 +
        promptLines * TITLE_LINE_PX +
        GAP_PX +
        node.options.length * OPTION_ROW_PX +
        (node.options.length - 1) * OPTION_GAP_PX;
      break;
    }
    default:
      return { lines: 0, limit };
  }
  return { lines: px / BODY_LINE_PX, limit };
}

/**
 * Warnings for every node whose estimated height exceeds what fits on the reference projector
 * (1280×900) without scrolling. Never `error`: a long node still plays, the facilitator just has
 * to scroll it, which is exactly what `.screen-main`'s scroll region and "more below" affordance
 * are for — but it is worth an author's attention.
 */
export function screenFitWarnings(s: Scenario): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  for (const [id, node] of Object.entries(s.nodes)) {
    const { lines, limit } = estimateScreenFit(node);
    if (lines > limit) {
      issues.push({
        level: "warning",
        path: `nodes.${id}`,
        message:
          `estimated ${Math.ceil(lines)} lines on the big screen at ${REFERENCE_WIDTH}×${REFERENCE_HEIGHT}, ` +
          `but only ${Math.floor(limit)} fit without scrolling — consider trimming it or splitting it`,
      });
    }
  }
  return issues;
}
